import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Fab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Sports,
  Add,
  Edit,
  Delete,
  Restore,
} from '@mui/icons-material';
import {
  useSports,
  useCreateSport,
  useUpdateSport,
  useDeleteSport,
  useRestoreSport,
} from '@/hooks/useApi';
import { LoadingState, ErrorState, ResponsiveDialog } from '@/components';
import { BOTTOM_NAV_OFFSET } from '@/components/BottomNav';
import type { SportType } from '@/types';
import { LISTED_SPORT_OPTIONS, getSportOption } from '@/utils/sports';

export const SportsPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSport, setEditingSport] = useState<{ id: string; name: string; code: SportType; active: boolean } | null>(null);
  const [formData, setFormData] = useState({ name: '', code: '' as SportType, active: true });

  const { data: sports, isLoading, error, refetch } = useSports();
  const createSport = useCreateSport();
  const updateSport = useUpdateSport();
  const deleteSport = useDeleteSport();
  const restoreSport = useRestoreSport();

  const handleOpenCreate = () => {
    setEditingSport(null);
    setFormData({ name: '', code: 'football', active: true });
    setOpenDialog(true);
  };

  const handleOpenEdit = (sport: { id: string; name: string; code: SportType; active: boolean }) => {
    setEditingSport(sport);
    setFormData({ name: sport.name, code: sport.code, active: sport.active });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSport(null);
  };

  const handleSubmit = async () => {
    if (editingSport) {
      await updateSport.mutateAsync({ id: editingSport.id, data: formData });
    } else {
      await createSport.mutateAsync(formData);
    }
    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Удалить вид спорта?')) {
      await deleteSport.mutateAsync(id);
    }
  };

  const handleRestore = async (id: string) => {
    await restoreSport.mutateAsync(id);
  };

  if (isLoading) return <LoadingState message="Загрузка видов спорта..." />;
  if (error) return <ErrorState message="Ошибка загрузки" onRetry={refetch} />;

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sports color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold">Виды спорта</Typography>
        </Box>
      </Box>

      <List sx={{ px: 1 }}>
        {sports?.map((sport) => (
          <Paper key={sport.id} sx={{ mb: 1 }}>
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{getSportOption(sport.code)?.icon || '🏆'}</span>
                    <Typography fontWeight="medium">{sport.name}</Typography>
                    {!sport.active && <Chip label="Неактивен" size="small" color="default" />}
                  </Box>
                }
                secondary={getSportOption(sport.code)?.label}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => handleOpenEdit(sport)} edge="end" sx={{ mr: 1 }}>
                  <Edit />
                </IconButton>
                {sport.active ? (
                  <IconButton onClick={() => handleDelete(sport.id)} edge="end" color="error">
                    <Delete />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => handleRestore(sport.id)} edge="end" color="success">
                    <Restore />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          </Paper>
        ))}
      </List>

      <Fab color="primary" sx={{ position: 'fixed', bottom: { xs: `calc(${BOTTOM_NAV_OFFSET} + 16px)`, lg: 16 }, right: 16 }} onClick={handleOpenCreate}>
        <Add />
      </Fab>

      <ResponsiveDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={editingSport ? 'Редактировать вид спорта' : 'Новый вид спорта'}
        actions={
          <>
            <Button onClick={handleCloseDialog} sx={{ minHeight: 48 }}>Отмена</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!formData.name} sx={{ minHeight: 48 }}>
              {editingSport ? 'Сохранить' : 'Создать'}
            </Button>
          </>
        }
      >
          <TextField
            autoFocus
            margin="dense"
            label="Название"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Код</InputLabel>
            <Select
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value as SportType })}
            >
              {LISTED_SPORT_OPTIONS.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Активен"
          />
      </ResponsiveDialog>
    </Box>
  );
};
